import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography, COLORS, SearchInput, Select, Option, Card, IconButton, Checkbox, Tag,
} from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useLazyGetExcludedLocationTreeQuery } from 'api/libraryApi';
import { Notify } from 'components/Notifications/Notifications';
import type { FlatTreeNode } from 'types';
import type { ExcludedLocation } from 'types/LibraryApi';
import TreeSelect from './TreeSelect';
import { getAllDescendantIds, buildBreadcrumb } from './excluded-locations/locationPickerUtils';

/**
 * Combined search + tree "Excluded locations" picker (pvs-design-system).
 *
 * One SearchInput; focusing/clicking (or typing) opens a dropdown containing the
 * checkbox location tree — or a flat filtered list while searching. Chosen
 * locations render below as cards, each with a per-selection scope
 * (this location / + all sublocations / sublocations only) and a remove button.
 *
 * Value is ExcludedLocation[] — the exact shape the save mutations consume, so the
 * three-way scope round-trips through the backend enabled/childrenEnabled flags.
 */

type Scope = 'selfAndSub' | 'self' | 'subOnly';

const SCOPE_OPTIONS: { value: Scope; label: string }[] = [
  { value: 'selfAndSub', label: 'This location + all sublocations' },
  { value: 'self', label: 'This location only' },
  { value: 'subOnly', label: 'Sublocations only' },
];

/** enabled/childrenEnabled flags → scope. */
const toScope = (loc: ExcludedLocation): Scope =>
  loc.enabled && loc.childrenEnabled ? 'selfAndSub' : loc.enabled ? 'self' : 'subOnly';

/** scope → ExcludedLocation (self=enabled, subs=childrenEnabled). */
const makeExcluded = (idLocation: number, scope: Scope): ExcludedLocation => ({
  idLocation,
  enabled: scope !== 'subOnly',
  childrenEnabled: scope !== 'self',
  overrideDenyAllChildren: false,
});

/** Star-styled pill dropdown for choosing a location's exclusion scope
 *  (this location / + all sublocations / sublocations only). Used on both the
 *  search-result rows and the selected cards. */
const ScopeSelect: React.FC<{
  id: string;
  scope: Scope;
  ariaLabel: string;
  onScopeChange: (scope: Scope) => void;
  minWidth?: number;
  hasSublocations?: boolean;
  disabled?: boolean;
}> = ({ id, scope, ariaLabel, onScopeChange, minWidth = 260, hasSublocations = true, disabled = false }) => (
  <Select
    id={id}
    value={scope}
    disabled={disabled}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScopeChange(e.target.value as Scope)}
    onClick={(e: React.MouseEvent) => e.stopPropagation()}
    size="small"
    aria-label={ariaLabel}
    SelectProps={{
      MenuProps: {
        PaperProps: {
          sx: {
            borderRadius: 2,
            '& .MuiMenuItem-root.Mui-selected': { backgroundColor: COLORS.purple[100], fontWeight: 600, color: COLORS.purple[800] },
            '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: COLORS.purple[100] },
          },
        },
      },
    }}
    sx={{
      width: minWidth, flexShrink: 0, background: COLORS.white,
      '& .MuiOutlinedInput-root': { borderRadius: '999px' },
      '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    }}
  >
    {SCOPE_OPTIONS.map((opt) => (
      <Option key={opt.value} value={opt.value} disabled={!hasSublocations && opt.value !== 'self'}>
        {opt.label}
      </Option>
    ))}
  </Select>
);

interface ExcludeLocationsFieldProps {
  value: ExcludedLocation[];
  locationId: number | null;
  onChange: (value: ExcludedLocation[]) => void;
  isCreateMode?: boolean;
  helperText?: React.ReactNode;
  /** When true the whole picker is read-only — used e.g. while an admin form is
   *  re-loading a different record and the value is about to be replaced. */
  disabled?: boolean;
}

const ExcludeLocationsField: React.FC<ExcludeLocationsFieldProps> = ({
  value, locationId, onChange, isCreateMode = true, helperText, disabled = false,
}) => {
  const [fetchTree] = useLazyGetExcludedLocationTreeQuery();
  const [nodes, setNodes] = useState<FlatTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showLocationTypes, setShowLocationTypes] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (locationId === null) { setNodes([]); return; }
    setLoading(true);
    fetchTree({ idTopLocation: locationId, isCreateMode })
      .unwrap()
      .then((result) =>
        setNodes(result.map((n) => ({
          id: n.id, name: n.name, idParent: n.idParent, hasChildren: n.hasChildren, locationType: n.locationType,
        }))),
      )
      .catch(() => { Notify.error('Failed to load locations. Please try again.'); setNodes([]); })
      .finally(() => setLoading(false));
  }, [locationId, isCreateMode, fetchTree]);

  // Close the tree dropdown on outside click (ignore clicks inside a portaled
  // MUI menu — e.g. an open scope dropdown — so picking a scope keeps it open).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !target.closest('.MuiPopover-root')
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // When the tree/results panel opens it can render below the modal's fold;
  // scroll it into view so the treeview is visible ("upscroll").
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() =>
      dropdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
    );
    return () => cancelAnimationFrame(id);
  }, [open]);

  // If the field is disabled while the dropdown is open (e.g. an admin form starts
  // re-loading a different record), close it — so the open-only effects above don't
  // linger on a hidden dropdown, and it doesn't reappear when the field re-enables.
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const selectedIds = useMemo(() => new Set(value.map((v) => v.idLocation)), [value]);
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // Effective exclusion set for the tree's [selected/total] counts: a location
  // counts as excluded if it's self-excluded, or cascade-excluded because it (or
  // an ancestor) has childrenEnabled (scope includes sublocations).
  const countIds = useMemo(() => {
    const set = new Set<number>();
    value.forEach((v) => {
      if (v.enabled) set.add(v.idLocation);
      if (v.childrenEnabled) {
        getAllDescendantIds(nodes, v.idLocation).forEach((id) => {
          if (id !== v.idLocation) set.add(id);
        });
      }
    });
    return set;
  }, [value, nodes]);

  const handleToggle = (id: number) => {
    if (selectedIds.has(id)) {
      onChange(value.filter((v) => v.idLocation !== id));
    } else {
      // Default a new pick to "This location only"; the user can widen the scope.
      onChange([makeExcluded(id, 'self'), ...value]);
    }
  };

  const handleScopeChange = (id: number, scope: Scope) =>
    onChange(value.map((v) => (v.idLocation === id ? makeExcluded(id, scope) : v)));

  const handleRemove = (id: number) => onChange(value.filter((v) => v.idLocation !== id));

  // Typing stays responsive by deferring the filter: the input value updates
  // immediately (high priority) while the potentially large filtered list
  // re-renders at a lower priority. `isSearching` uses the immediate text so we
  // never fall back to rendering the (heavy) full tree mid-keystroke.
  const isSearching = searchText.trim().length > 0;
  const deferredSearchText = useDeferredValue(searchText);
  const isStale = deferredSearchText !== searchText;
  const query = deferredSearchText.trim().toLowerCase();
  const searchMatches = useMemo(
    () => (query ? nodes.filter((n) => n.name.toLowerCase().includes(query)) : []),
    [query, nodes],
  );
  // Cap rendered rows — a broad query can match thousands of nodes, and
  // rendering every match (each a checkbox row) is what makes typing stutter.
  const MAX_RESULTS = 50;
  const visibleMatches = useMemo(() => searchMatches.slice(0, MAX_RESULTS), [searchMatches]);

  const labelEl = (
    <Typography variant="label-md" component="label" style={{ color: COLORS.gray[900], display: 'block' }}>
      Excluded locations
    </Typography>
  );

  if (locationId === null) {
    return (
      <Box>
        {labelEl}
        <Typography variant="p-xs" style={{ color: COLORS.gray[400], display: 'block', marginTop: 4 }}>
          Select a location first to choose exclusions.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {labelEl}
      {helperText && (
        <Typography variant="p-xs" style={{ color: COLORS.gray[400], display: 'block', margin: '4px 0 0' }}>
          {helperText}
        </Typography>
      )}

      {/* Search input + tree dropdown */}
      <Box ref={containerRef} sx={{ position: 'relative', mt: 1, display: 'flex', flexDirection: 'column' }}>
        <SearchInput
          id="exclude-locations-search"
          name="exclude-locations-search"
          ariaLabel="Search locations to exclude"
          placeholder="Type a location…"
          value={searchText}
          size="medium"
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { if (disabled) return; setSearchText(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) setOpen(true); }}
          onClick={() => { if (!disabled) setOpen(true); }}
          sx={{ width: '100%', order: 0 }}
        />

        {/* Selected locations — listed beneath the tree (per UX): the treeview
            stays on top, the chosen locations stack below it. */}
        {value.length > 0 && (
          <Box sx={{ mt: 1, order: 2 }}>
            <Typography variant="p-sm" component="span" style={{ color: COLORS.gray[700], display: 'block', fontWeight: 600, marginBottom: 4 }}>
              Selected ({value.length})
            </Typography>
            <Stack sx={{ gap: 1, maxHeight: 260, overflowY: 'auto', p: 1, border: `1px solid ${COLORS.gray[200]}`, borderRadius: 1, background: COLORS.gray[100] }}>
              {value.map((loc) => {
                const scope = toScope(loc);
                const node = nodeById.get(loc.idLocation);
                const subCount = Math.max(0, getAllDescendantIds(nodes, loc.idLocation).length - 1);
                const description = scope === 'selfAndSub'
                  ? `Excludes this location + ${subCount.toLocaleString()} sublocations`
                  : scope === 'self'
                    ? 'Excludes this location only'
                    : `Excludes ${subCount.toLocaleString()} sublocations only`;
                return (
                  <Card
                    key={loc.idLocation}
                    variant="outlined"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderColor: COLORS.gray[200], boxShadow: 'none', borderRadius: 1, flexShrink: 0, background: COLORS.white }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="p-md-semiBold" title={node?.name ?? `Location #${loc.idLocation}`} style={{ color: COLORS.gray[900], display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {node?.name ?? `Location #${loc.idLocation}`}
                      </Typography>
                      <Typography variant="p-sm" style={{ color: COLORS.gray[800], display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {description}
                      </Typography>
                    </Box>
                    <ScopeSelect
                      id={`exclude-scope-${loc.idLocation}`}
                      scope={scope}
                      ariaLabel={`Exclusion scope for ${node?.name ?? 'location'}`}
                      onScopeChange={(s) => handleScopeChange(loc.idLocation, s)}
                      hasSublocations={subCount > 0}
                      disabled={disabled}
                    />
                    <IconButton
                      variant="secondary"
                      aria-label={`Remove ${node?.name ?? 'location'}`}
                      onClick={() => handleRemove(loc.idLocation)}
                      disabled={disabled}
                      sx={{ flexShrink: 0, color: COLORS.purple[700], '&:hover': { background: COLORS.purple[100], color: COLORS.purple[800] } }}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        )}

        {open && !disabled && (
          <Box
            ref={dropdownRef}
            sx={{
              mt: 1, order: 1,
              background: COLORS.white, border: `1px solid ${COLORS.gray[200]}`, borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ maxHeight: 300, overflowY: 'auto', p: 0.5 }}>
              {loading ? (
                <Typography variant="p-sm" style={{ padding: 16, color: COLORS.gray[400], fontStyle: 'italic' }}>Loading locations…</Typography>
              ) : isSearching ? (
                searchMatches.length === 0 ? (
                  <Typography variant="p-sm" style={{ padding: 16, color: COLORS.gray[400] }}>
                    {isStale ? 'Searching…' : 'No locations found.'}
                  </Typography>
                ) : (
                  <>
                  {visibleMatches.map((n) => {
                    // Leaf name on top, ancestor path (breadcrumb minus the leaf) below.
                    const ancestorPath = buildBreadcrumb(n, nodes).split(' > ').slice(0, -1).join(' > ');
                    const isSelected = selectedIds.has(n.id);
                    return (
                      <Box
                        key={n.id}
                        onClick={() => handleToggle(n.id)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, cursor: 'pointer',
                          borderBottom: `1px solid ${COLORS.gray[100]}`,
                          background: isSelected ? COLORS.purple[100] : 'transparent',
                          '&:hover': { background: isSelected ? COLORS.purple[100] : COLORS.gray[100] },
                        }}
                      >
                        <Checkbox
                          id={`exclude-search-${n.id}`}
                          checked={isSelected}
                          onChange={() => handleToggle(n.id)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          aria-label={n.name}
                          sx={{ padding: 0 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="p-md-semiBold" component="span" style={{ color: COLORS.gray[900] }}>
                              {n.name}
                            </Typography>
                            {showLocationTypes && n.locationType && (
                              <Tag
                                label={n.locationType}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: COLORS.gray[400], '& span': { color: COLORS.gray[900] } }}
                              />
                            )}
                          </Box>
                          {ancestorPath && (
                            <Typography variant="p-sm" component="span" style={{ color: COLORS.gray[700], display: 'block' }}>
                              {ancestorPath}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {searchMatches.length > visibleMatches.length && (
                    <Typography variant="p-xs" style={{ padding: '8px 16px', color: COLORS.gray[400], display: 'block' }}>
                      Showing first {visibleMatches.length} of {searchMatches.length.toLocaleString()}. Keep typing to narrow results.
                    </Typography>
                  )}
                  </>
                )
              ) : nodes.length === 0 ? (
                <Typography variant="p-sm" style={{ padding: 16, color: COLORS.gray[400] }}>No locations available.</Typography>
              ) : (
                <TreeSelect
                  variant="inline"
                  multiSelect
                  nodes={nodes}
                  selectedIds={selectedIds}
                  onToggle={handleToggle}
                  showLocationType={showLocationTypes}
                  showCount
                  allFlatNodes={nodes}
                  countIds={countIds}
                />
              )}
            </Box>
            {/* Show Location Types toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 0.75, borderTop: `1px solid ${COLORS.gray[200]}`, background: COLORS.gray[100] }}>
              <Checkbox
                id="exclude-show-location-types"
                checked={showLocationTypes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowLocationTypes(e.target.checked)}
                label="Show Location Types"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(ExcludeLocationsField);
